import { useEffect } from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { ContextProduct } from './context-product';
import { ContextSearch } from './context-search';
import { useProductSwitcher } from './product-switcher-context';

function isShortcut(event: KeyboardEvent): boolean {
  return (event.metaKey || event.ctrlKey) && !event.altKey && event.key.toLowerCase() === 'k';
}

/**
 * Where you are and where you can go, in one bar. The two halves are two controls — one
 * searches a list, the other opens the products — sharing a single border so they read as
 * one thing. The shortcut belongs to the bar rather than either half.
 */
export function ContextBar() {
  const switcher = useProductSwitcher();
  const isOpen = switcher !== null && switcher.isPanelOpen;
  const togglePanel = switcher?.togglePanel;

  useEffect(() => {
    if (!togglePanel) {
      return undefined;
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (isShortcut(event) && togglePanel) {
        event.preventDefault();
        togglePanel('organizations');
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [togglePanel]);

  if (switcher === null || switcher.product.length === 0) {
    return null;
  }

  // Closed, the bar is a control and takes a focus ring. Open, it is the top of one sheet: a
  // ring around half of it, or a line where the two meet, would read as two stacked things.
  let borderSx: Record<string, unknown> = {
    borderColor: 'divider',
    '&:hover': { borderColor: 'primary.main' },
    '&:focus-within': { borderColor: 'primary.main' },
  };
  if (isOpen) {
    borderSx = { borderColor: 'divider', borderBottomColor: 'transparent' };
  }

  return (
    <Box
      ref={switcher.barRef}
      sx={{
        width: '100%',
        minWidth: 0,
        height: 38,
        display: 'flex',
        alignItems: 'stretch',
        // Open, the bar is the top of the panel rather than a control above it: it keeps its own
        // top corners and hands the bottom ones to the sheet unrolling out of it.
        borderRadius: isOpen ? '16px 16px 0 0' : '16px',
        border: '1px solid',
        overflow: 'hidden',
        // The bar is the top of the sheet, so it carries the sheet's surface, not the page's.
        bgcolor: 'var(--color-backdrop-secondary)',
        ...borderSx,
      }}
    >
      <ContextSearch />
      <Divider orientation="vertical" flexItem />
      <ContextProduct />
    </Box>
  );
}
